import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { leaveTextEditing, type LeaveTextEditingResponse } from './api';

/**
 * Screen-3 leave-text-editing mutation hook.
 *
 * Fires `POST /api/book/orders/:orderId/text-editing/leave` when the
 * customer clicks the Weiter button. On success the server transitions
 * `text_editing → preview_rendering` and enqueues ONE bundled re-
 * extraction. BookRoute re-dispatches to `PreviewWaitScreen` when the
 * caller's refetch resolves with the new status.
 *
 * Same result shape as the other screen-3 hooks — the caller can
 * branch on `error_code` inline for any transient error.
 */

export type LeaveTextEditingResult =
  | { readonly ok: true; readonly response: LeaveTextEditingResponse }
  | { readonly ok: false; readonly error: ApiError | null };

export interface UseLeaveTextEditingState {
  readonly leaving: boolean;
  readonly error: ApiError | null;
  readonly leave: () => Promise<LeaveTextEditingResult>;
}

export function useLeaveTextEditing(orderId: string): UseLeaveTextEditingState {
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const leave = useCallback(async (): Promise<LeaveTextEditingResult> => {
    setLeaving(true);
    setError(null);
    try {
      const response = await leaveTextEditing(orderId);
      return { ok: true, response };
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      setError(apiError);
      return { ok: false, error: apiError };
    } finally {
      setLeaving(false);
    }
  }, [orderId]);

  return { leaving, error, leave };
}
